import { StandardEvents } from '@shopify/events';

const PICKER_SELECTOR = "variant-picker[data-elavisse-size-required='true']";
const SIZE_RADIO_SELECTOR =
  "[data-elavisse-size-option='true'] input[type='radio'][data-elavisse-size-input='true']";
const SIZE_SELECT_SELECTOR =
  "[data-elavisse-size-option='true'] select[data-elavisse-size-select='true']";
const ERROR_MESSAGE = 'Please select your size.';
const PENDING_MESSAGE = 'Please wait while your size is updated.';
const sizeStates = new Map();

function stateKey(picker) {
  return (picker.dataset.sectionId || 'section') + ':' + (picker.dataset.productId || 'product');
}

function getContext(element) {
  return (
    element.closest('.shopify-section, featured-product-information, quick-add-dialog, dialog, product-card') ||
    document
  );
}

function matchingForms(picker) {
  const productId = picker.dataset.productId;
  return Array.from(
    getContext(picker).querySelectorAll("product-form-component[data-elavisse-size-required='true']")
  ).filter((form) => !productId || form.dataset.productId === productId);
}

function matchingStickyBars(picker) {
  const productId = picker.dataset.productId;
  return Array.from(document.querySelectorAll('sticky-add-to-cart')).filter(
    (sticky) => !productId || sticky.dataset.productId === productId
  );
}

function sizeRadios(picker) {
  return Array.from(picker.querySelectorAll(SIZE_RADIO_SELECTOR));
}

function sizeSelects(picker) {
  return Array.from(picker.querySelectorAll(SIZE_SELECT_SELECTOR));
}

function sizeControls(picker) {
  return [...sizeRadios(picker), ...sizeSelects(picker)];
}

function selectedSize(picker) {
  const radio = picker.querySelector(SIZE_RADIO_SELECTOR + ':checked');
  if (radio) return radio;

  const select = picker.querySelector(SIZE_SELECT_SELECTOR);
  if (!select?.value) return null;
  return select.selectedOptions[0] || null;
}

function errorElement(picker) {
  return picker.querySelector('[data-elavisse-size-error]');
}

function setError(picker, message) {
  const fieldset = picker.querySelector("[data-elavisse-size-option='true']");
  const error = errorElement(picker);
  if (fieldset) fieldset.dataset.elavisseSizeError = 'true';
  for (const control of sizeControls(picker)) control.setAttribute('aria-invalid', 'true');
  if (error) {
    error.textContent = message;
    error.hidden = false;
  }
}

function clearError(picker) {
  const fieldset = picker.querySelector("[data-elavisse-size-option='true']");
  const error = errorElement(picker);
  if (fieldset) fieldset.removeAttribute('data-elavisse-size-error');
  for (const control of sizeControls(picker)) control.removeAttribute('aria-invalid');
  if (error) {
    error.textContent = '';
    error.hidden = true;
  }
}

function setState(picker, status, variantId) {
  const key = stateKey(picker);
  const next = { status: status, variantId: variantId || '' };
  sizeStates.set(key, next);
  picker.dataset.elavisseSizeState = status;

  for (const form of matchingForms(picker)) {
    form.dataset.elavisseSizeState = status;
    const idInput = form.querySelector("input[name='id'][ref='variantId']");
    if (!idInput) continue;

    if (status === 'unselected') {
      idInput.disabled = true;
      idInput.dataset.elavisseSizeLocked = 'true';
    } else {
      idInput.disabled = false;
      idInput.removeAttribute('data-elavisse-size-locked');
      if (variantId) idInput.value = variantId;
    }
  }

  for (const sticky of matchingStickyBars(picker)) {
    sticky.dataset.elavisseSizeState = status;
    sticky.hidden = status === 'unselected';

    const stickyVariantTitle = sticky.querySelector('[data-testid="sticky-variant-title"]');
    if (stickyVariantTitle) {
      const selectedSizeValue = selectedSize(picker)?.value || '';
      stickyVariantTitle.textContent =
        status === 'unselected' ? 'Select size' : selectedSizeValue || stickyVariantTitle.textContent.trim();
    }

    const stickyButton = sticky.querySelector('.sticky-add-to-cart__button');
    if (stickyButton && stickyButton.dataset.elavisseSizeGuardBound !== 'true') {
      stickyButton.dataset.elavisseSizeGuardBound = 'true';
      stickyButton.addEventListener(
        'click',
        (event) => {
          const currentPicker = pickerForControl(stickyButton);
          if (!currentPicker) return;

          const selected = selectedSize(currentPicker);
          const current = currentState(currentPicker);
          if (!selected || current.status === 'unselected') {
            blockPurchase(event, currentPicker, ERROR_MESSAGE);
          } else if (current.status === 'pending') {
            blockPurchase(event, currentPicker, PENDING_MESSAGE);
          }
        },
        true
      );
    }
  }
}

function currentState(picker) {
  return sizeStates.get(stateKey(picker)) || { status: 'unselected', variantId: '' };
}

function clearDefaultSelection(picker) {
  for (const input of sizeRadios(picker)) {
    input.checked = false;
    input.removeAttribute('checked');
    input.dataset.currentChecked = 'false';
    input.dataset.previousChecked = 'false';
  }
  for (const select of sizeSelects(picker)) select.value = '';
}

function restoreStoredSelection(picker, variantId) {
  if (!variantId) return false;

  const radio = sizeRadios(picker).find((input) => input.dataset.variantId === variantId);
  if (radio) {
    radio.checked = true;
    radio.dataset.currentChecked = 'true';
    return true;
  }

  for (const select of sizeSelects(picker)) {
    const option = Array.from(select.options).find((item) => item.dataset.variantId === variantId);
    if (!option) continue;
    select.value = option.value;
    return true;
  }

  return false;
}

function initializePicker(picker) {
  if (!(picker instanceof HTMLElement)) return;

  const key = stateKey(picker);
  const existing = sizeStates.get(key);
  if (existing) {
    clearDefaultSelection(picker);
    const restored = existing.status !== 'unselected' && restoreStoredSelection(picker, existing.variantId);
    setState(picker, restored ? existing.status : 'unselected', restored ? existing.variantId : '');
    clearError(picker);
    return;
  }

  const selected = selectedSize(picker);
  const variantParameter =
    picker.dataset.templateProductMatch === 'true'
      ? new URL(window.location.href).searchParams.get('variant')
      : null;
  const selectedVariantId = selected?.dataset.variantId || '';
  const hasIntentionalVariant = Boolean(
    variantParameter && selectedVariantId && variantParameter === selectedVariantId
  );

  if (hasIntentionalVariant) {
    setState(picker, 'selected', selectedVariantId);
  } else {
    clearDefaultSelection(picker);
    setState(picker, 'unselected', '');
  }

  clearError(picker);
}

function initializeWithin(root) {
  if (root instanceof Element && root.matches(PICKER_SELECTOR)) initializePicker(root);
  for (const picker of root.querySelectorAll?.(PICKER_SELECTOR) || []) initializePicker(picker);
}

function pickerForControl(control) {
  const context = getContext(control);
  const productId =
    control.closest('product-form-component, sticky-add-to-cart')?.dataset.productId || '';
  const matchesProduct = (picker) => !productId || picker.dataset.productId === productId;
  const localPicker = Array.from(context.querySelectorAll(PICKER_SELECTOR)).find(matchesProduct);
  if (localPicker) return localPicker;

  return Array.from(document.querySelectorAll(PICKER_SELECTOR)).find(matchesProduct) || null;
}

function blockPurchase(event, picker, message) {
  event.preventDefault();
  event.stopImmediatePropagation();
  setError(picker, message);
  const firstInput = sizeControls(picker)[0];
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  picker.scrollIntoView({ behavior: reduceMotion ? 'auto' : 'smooth', block: 'center' });
  window.setTimeout(() => firstInput?.focus({ preventScroll: true }), reduceMotion ? 0 : 250);
}

document.addEventListener(
  'submit',
  (event) => {
    const form = event.target;
    if (!(form instanceof HTMLFormElement)) return;
    const component = form.closest("product-form-component[data-elavisse-size-required='true']");
    if (!component) return;

    const picker = pickerForControl(component);
    if (!picker) return;

    const selected = selectedSize(picker);
    const state = currentState(picker);
    if (!selected || state.status === 'unselected') {
      blockPurchase(event, picker, ERROR_MESSAGE);
    } else if (state.status === 'pending') {
      blockPurchase(event, picker, PENDING_MESSAGE);
    }
  },
  true
);

document.addEventListener(
  'click',
  (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;

    const guardedControl = target.closest(
      '.sticky-add-to-cart__button, .accelerated-checkout-block button, .shopify-payment-button__button'
    );
    if (!guardedControl) return;

    const picker = pickerForControl(guardedControl);
    if (!picker) return;

    const selected = selectedSize(picker);
    const state = currentState(picker);
    if (!selected || state.status === 'unselected') {
      blockPurchase(event, picker, ERROR_MESSAGE);
    } else if (state.status === 'pending') {
      blockPurchase(event, picker, PENDING_MESSAGE);
    }
  },
  true
);

document.addEventListener(StandardEvents.productSelect, (event) => {
  const picker =
    event.target instanceof Element
      ? event.target.closest(PICKER_SELECTOR)
      : null;
  if (!picker) return;

  const selected = selectedSize(picker);
  if (!selected) {
    setState(picker, 'unselected', '');
    return;
  }

  const expectedVariantId = selected.dataset.variantId || '';
  clearError(picker);
  setState(picker, 'pending', expectedVariantId);

  event.promise
    ?.then(({ detail }) => {
      const currentSelection = selectedSize(picker);
      if (!currentSelection || currentSelection.dataset.variantId !== expectedVariantId) return;

      const resolvedVariantId = detail?.resource?.id ? String(detail.resource.id) : '';
      if (resolvedVariantId && (!expectedVariantId || resolvedVariantId === expectedVariantId)) {
        setState(picker, 'selected', resolvedVariantId);
        clearError(picker);
      } else {
        setState(picker, 'unselected', '');
      }
    })
    .catch((error) => {
      if (error?.name === 'AbortError') return;
      if (selectedSize(picker)?.dataset.variantId === expectedVariantId) {
        setState(picker, 'unselected', '');
      }
    });
});

document.addEventListener('shopify:section:load', (event) => initializeWithin(event.target));
window.addEventListener('pageshow', () => initializeWithin(document));

const observer = new MutationObserver((records) => {
  for (const record of records) {
    for (const node of record.addedNodes) {
      if (node instanceof Element) initializeWithin(node);
    }
  }
});

initializeWithin(document);
observer.observe(document.documentElement, { childList: true, subtree: true });
